import { useState, useEffect } from 'react';
import { auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { getEnv } from '@/utils/env';

export interface AuthUser {
  uid: string;
  email: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  phone?: string;
}

export const useAuth = () => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [firebaseAuthReady, setFirebaseAuthReady] = useState(false);

  useEffect(() => {
    // Subscribe to Firebase Auth state so the hook reflects the real client auth state
    console.log('🔐 Initializing authentication listener...');
    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      try {
        const userStr = localStorage.getItem('user');
        if (fbUser) {
          // Firebase client authenticated
          setFirebaseAuthReady(true);
          // If we have local storage user data prefer that shape
          if (userStr) {
            setUser(JSON.parse(userStr));
          } else {
            // fallback to basic info
            setUser({ uid: fbUser.uid, email: fbUser.email ?? '' });
          }
        } else {
          // Not signed in on Firebase client - but localStorage may still have user metadata
          setFirebaseAuthReady(false);
          if (userStr) {
            setUser(JSON.parse(userStr));
          } else {
            setUser(null);
          }
        }
      } catch (err) {
        console.error('❌ onAuthStateChanged handler error:', err);
        setUser(null);
        setFirebaseAuthReady(false);
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const logout = async () => {
    try {
      localStorage.removeItem('user');
      localStorage.removeItem('idToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('authTokens');
      localStorage.removeItem('token');
      // Clear data-room module-unlock signals so they don't leak to the next user
      localStorage.removeItem('dr_hasAccounting');
      localStorage.removeItem('dr_hasConsulting');
      // Clear view/onboarding-routing flags so the next account on this browser is not
      // misrouted (e.g. landing on the previous user's accounting/consulting view).
      localStorage.removeItem('activeServiceView');
      localStorage.removeItem('setupService');
      localStorage.removeItem('signupUserType');
      localStorage.removeItem('operateOnboardingComplete');
      setUser(null);
      setFirebaseAuthReady(false);

      // Sign out from Firebase Auth
      if (auth.currentUser) {
        await auth.signOut();
      }

      console.log('✅ Logged out successfully');
    } catch (error) {
      console.error('❌ Logout error:', error);
    }
  };

  const refreshAuth = async () => {
    const userStr = localStorage.getItem('user');
    const idToken = localStorage.getItem('idToken');
    if (userStr) {
      const userData = JSON.parse(userStr);
      setUser(userData);
    }

    // Refresh ID token using refresh token exchange for backend REST calls
    // AccountingLogin/ConsultingLogin save the refresh token inside authTokens JSON,
    // not as a separate key — read both locations.
    const authTokensRaw = localStorage.getItem('authTokens');
    const authTokensParsed = authTokensRaw ? (() => { try { return JSON.parse(authTokensRaw); } catch { return null; } })() : null;
    const refreshToken = localStorage.getItem('refreshToken') || authTokensParsed?.refreshToken || null;
    const apiKey = getEnv('VITE_FIREBASE_API_KEY', '');
    if (refreshToken && apiKey) {
      try {
        const params = new URLSearchParams();
        params.append('grant_type', 'refresh_token');
        params.append('refresh_token', refreshToken);

        const resp = await fetch(`https://securetoken.googleapis.com/v1/token?key=${apiKey}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: params.toString()
        });

        const body = await resp.json();
        if (resp.ok && body.id_token) {
          // Update individual token keys
          localStorage.setItem('idToken', body.id_token);
          localStorage.setItem('token', body.id_token); // Sync with utils/auth.ts
          localStorage.setItem('refreshToken', body.refresh_token || refreshToken);

          // Update the full authTokens object which DataRoom uses
          const authTokensStr = localStorage.getItem('authTokens');
          if (authTokensStr) {
            try {
              const authTokens = JSON.parse(authTokensStr);
              authTokens.idToken = body.id_token;
              authTokens.refreshToken = body.refresh_token || refreshToken;
              localStorage.setItem('authTokens', JSON.stringify(authTokens));
            } catch (e) {
              console.warn('Failed to parse/update authTokens JSON', e);
            }
          }

          console.log('🔁 ID token refreshed and storage synced');
          setFirebaseAuthReady(!!auth.currentUser);
          return true;
        } else {
          console.warn('⚠️ Failed to refresh token', body);
          return false;
        }
      } catch (err) {
        console.error('❌ Error refreshing token:', err);
        return false;
      }
    }
    return false;
  };

  return {
    user,
    loading,
    firebaseAuthReady,
    isAuthenticated: !!user,
    logout,
    refreshAuth
  };
};
